
import React, { useState, useMemo, useEffect } from "react";
import { Card, Container, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaEdit, FaTrash } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getvehicleType,
  deletevehicleType,
} from "../../store/slices/vehicletype";
import DeleteModal from "./deletepopup";

export default function Vehicle() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { profile } = useSelector((state) => state.auth);
  const { vehicleTypeList = [], loading } = useSelector(
    (state) => state.vehicleType
  );

  const isStaff =
    profile?.userType === "STAFF" || profile?.role === "STAFF";

  // Vehicle permission (not activity permission)
  const vehiclePermission = profile?.permissions?.vehicle;

  const permissions = Array.isArray(vehiclePermission)
    ? vehiclePermission
    : [vehiclePermission];

  const hasLevel = (levels) => {
    if (!isStaff) return true;

    return permissions.some((permission) =>
      levels.includes(String(permission || "").toUpperCase())
    );
  };

  const canView = hasLevel(["VIEW", "EDIT", "FULL"]);
  const canAdd = hasLevel(["EDIT", "FULL"]);
  const canEdit = hasLevel(["EDIT", "FULL"]);
  const canDelete = hasLevel(["FULL"]);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [showDelete, setShowDelete] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState("confirm");

  const pagelimit = 25;

  const fetchvehicle = async () => {
    try {
      await dispatch(getvehicleType()).unwrap();
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchvehicle();
    }
  }, [dispatch, canView]);

  const filteredData = useMemo(() => {
    return vehicleTypeList.filter((item) =>
      item.name?.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, vehicleTypeList]);

  const totalPages = Math.ceil(filteredData.length / pagelimit);

  const paginatedData = filteredData.slice(
    (currentPage - 1) * pagelimit,
    currentPage * pagelimit
  );

  const handleConfirmDelete = async () => {
    if (!canDelete || !selectedId) return;

    try {
      setDeleteStatus("loading");

      await dispatch(deletevehicleType(selectedId)).unwrap();

      setDeleteStatus("success");

      setTimeout(() => {
        setShowDelete(false);
        setSelectedId(null);
        setDeleteStatus("confirm");
      }, 1500);
    } catch (error) {
      console.log(error);
      setDeleteStatus("confirm");
    }
  };

  const changePage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Access denied
  if (!canView) {
    return (
      <Container fluid>
        <Card className="table-border mb-5">
          <Card.Body className="text-center py-5">
            <h5>Access Denied</h5>
            <p className="text-muted mb-0">
              You do not have permission to view vehicles.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <>
      <div className="breadcrumb-bar mb-2">
        <div className="breadcrumb-left">
          <Link to="/admin/home" className="breadcrumb-link active">
            Home
          </Link>

          <span className="breadcrumb-separator">|</span>

          <span
            className="breadcrumb-link inactive"
            onClick={() => navigate("/admin/vehicle")}
          >
            Vehicles
          </span>
        </div>

        {canAdd && (
          <button
            className="add-outlet-btn"
            onClick={() => navigate("/admin/addvehicle")}
          >
            + Add Vehicle
          </button>
        )}
      </div>

      <Container fluid>
        <Card className="table-border mb-5">
          <Card.Body>
            <div className="common-table-wrapper">
              <table className="common-table">
                <thead>
                  <tr>
                    <th className="text-center">#</th>
                    <th className="text-center">Name</th>
                    <th className="text-center">Description</th>
                    <th className="text-center">Details</th>

                    {(canEdit || canDelete) && (
                      <th className="text-center">Action</th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    [...Array(6)].map((_, index) => (
                      <tr key={index}>
                        <td className="text-center">
                          <div className="skeleton skeleton-text small"></div>
                        </td>

                        <td className="text-center">
                          <div className="skeleton skeleton-text"></div>
                        </td>

                        <td className="text-center">
                          <div className="skeleton skeleton-text"></div>
                        </td>

                        <td className="text-center">
                          <div className="d-flex flex-column align-items-center gap-1">
                            <div className="skeleton skeleton-text small"></div>
                            <div className="skeleton skeleton-text small"></div>
                          </div>
                        </td>

                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-2">
                              <div className="skeleton skeleton-btn"></div>
                              <div className="skeleton skeleton-btn"></div>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => (
                      <tr key={item._id}>
                        <td className="text-center">
                          {(currentPage - 1) * pagelimit + index + 1}
                        </td>

                        <td
                          className="text-center"
                          style={{
                            maxWidth: "200px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.name}
                        </td>

                        <td className="text-center">
                          {item.description?.length > 30 ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>{item.description}</Tooltip>}
                            >
                              <span>
                                {item.description.substring(0, 30)}...
                              </span>
                            </OverlayTrigger>
                          ) : (
                            item.description || "-"
                          )}
                        </td>

                        <td className="text-center">
                          <div className="d-flex flex-column">
                            <span>
                              <strong>Capacity:</strong> {item.capacity}
                            </span>
                            <span>
                              <strong>Fuel:</strong> {item.fuelType}
                            </span>
                          </div>
                        </td>

                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="action-btn-group">
                              {canEdit && (
                                <button
                                  onClick={() =>
                                    navigate(`/admin/editvehicle/${item._id}`)
                                  }
                                  className="action-btn edit-btn"
                                  title="Edit"
                                >
                                  <FaEdit size={14} />
                                </button>
                              )}

                              {canDelete && (
                                <button
                                  className="action-btn delete-btn"
                                  title="Delete"
                                  onClick={() => {
                                    setSelectedId(item._id);
                                    setShowDelete(true);
                                  }}
                                >
                                  <FaTrash size={14} />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={canEdit || canDelete ? 5 : 4}
                        className="text-center py-4"
                      >
                        No vehicles found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-end mt-3 gap-2">
                <button
                  onClick={() => changePage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="previous-btn"
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => changePage(i + 1)}
                    className={`button-between ${currentPage === i + 1 ? "active-page" : ""
                      }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => changePage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="next-btn"
                >
                  Next
                </button>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {canDelete && (
        <DeleteModal
          show={showDelete}
          onHide={() => {
            setShowDelete(false);
            setSelectedId(null);
            setDeleteStatus("confirm");
          }}
          onConfirm={handleConfirmDelete}
          status={deleteStatus}
        />
      )}
    </>
  );
}
